import React, { useState, useMemo } from 'react'
import { Dropdown, Modal, Table, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import Cards from '../../components/Cards'
import { useDeleteFeedbackChatMutation, useGetFeedbackChatForAllUsersQuery, useGetFeedbackChatQuery, useUpdateCommentTypeMutation } from '../../services/dashboardSlice'
import { PillsBadge } from '../../components/Badge'
import { dateFormater } from '../../helper'
import { useGetGeneralSettingsQuery } from '../../services/settingsSlice'
import { TabSlider } from '../../components/HeaderNav/Tabs'
import InputField from '../../components/InputField'
import { useForm } from 'react-hook-form'
import Icons from '../../components/Icons'
import { SearchIcon, TrashIcon } from '../../components/Icons/iconImages'
import { Button } from '../../components/Buttons'
import IconRings from '../../components/IconRings'
import { Link } from 'react-router-dom'
import { toast } from '../../components/Toast/toast'

interface CommentData {
  chat_id: string
  timestamp: string
  comment: string
  comment_status: string
}

function FeedbackHistory() {

  const { data: userData } = useGetGeneralSettingsQuery('', {
    refetchOnMountOrArgChange: true,
  });

  const localEmail = localStorage.getItem("oogw.ai_user_email");
  const getEmail = (!localEmail || localEmail === "null") ? userData?.settings?.email : localEmail;

  const oogwaiUser = getEmail && getEmail !== "info@oogw.ai" && getEmail !== "demo@oogw.ai" && getEmail.endsWith('@oogw.ai') ? true : false;

  const [deleteFeedbackChat] = useDeleteFeedbackChatMutation({})

  function useFeedbackData() {

    const queryForOogwai = useGetFeedbackChatForAllUsersQuery({}, { skip: !oogwaiUser });
    const queryForNormal = useGetFeedbackChatQuery({}, { skip: oogwaiUser });

    return oogwaiUser ? queryForOogwai : queryForNormal;
  }

  const { data: feedbackData, isLoading: isFeedbackDataLoading, error: isError, refetch: refetchFeedbackData } = useFeedbackData();

  const [updateCommentType, { isLoading: isUpdating }] = useUpdateCommentTypeMutation({})

  // State for filters and search
  const [statusFilter, setStatusFilter] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')

  // Form control for InputField
  const { control, watch } = useForm({
    defaultValues: {
      search: ''
    }
  })

  // Watch the search field and update searchTerm state
  const searchValue = watch('search')
  React.useEffect(() => {
    setSearchTerm(searchValue || '')
  }, [searchValue])

  const getStatusVariant = (status: string): 'green' | 'red' | 'blue' => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'red'
      case 'done':
        return 'green'
      case 'verified':
        return 'blue'
      default:
        return 'blue'
    }
  }

  const handleStatusUpdate = async (chatId: string, timestamp: string, newStatus: string, comment: string) => {
    if (!oogwaiUser) return;
    try {
      const response = await updateCommentType({
        chat_id: chatId,
        comment,
        timestamp,
        comment_status: newStatus
      }).unwrap()

      message.success(response?.message || 'Status updated successfully')
      refetchFeedbackData();
    } catch (error) {
      message.error('Failed to update status')
      console.error('Error updating status:', error)
    }
  }

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ chatId: string; timestamp: string } | null>(null)

  const confirmDelete = (chatId: string, timestamp: string) => {
    setDeleteTarget({ chatId, timestamp })
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteFeedbackChat({
        chat_id: deleteTarget.chatId,
        timestamp: deleteTarget.timestamp,
      }).unwrap()
      message.success('Feedback deleted successfully')
      refetchFeedbackData()
    } catch (error) {
      message.error('Failed to delete feedback')
      console.error('Error deleting feedback:', error)
    } finally {
      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
    }
  }

  const columns: ColumnsType<CommentData> = [
    {
      title: 'Chat ID',
      dataIndex: 'chat_id',
      key: 'chat_id',
      width: '5%',
      render: (chat_id: string, record: any) => {
        const handleClick = () => {
          toast.info('Redirecting to conversation...', 2)
          if (oogwaiUser) {
            toast.info('Impersonating as ' + record?.user_email, 2)
            localStorage.setItem('oogw.ai_shadow_user_email', record?.user_email || '')
            sessionStorage.removeItem('referral_code')
            localStorage.removeItem('workspace_id')
          }
          setTimeout(() => {
            window.location.href = `/conversation?chatid=${chat_id}`
          }, 2000)
        }
        return (
          <a onClick={handleClick}>{chat_id}</a>
        )
      },

    },
    ...(oogwaiUser
      ? [
        {
          title: 'User Email',
          dataIndex: 'user_email',
          key: 'user_email',
          width: '10%',
          render: (email: string) => <p className="truncate">{email || '-'}</p>,
        },
      ]
      : []),
    {
      title: oogwaiUser ? 'Users Comment' : 'Your Comment',
      dataIndex: 'comment',
      key: 'comment',
      width: '30%',
      ellipsis: true,
      render: (comment: string) => <p className="max-w-24">{comment}</p>,
    },
    {
      title: 'Status',
      dataIndex: 'comment_status',
      width: '3%',
      key: 'comment_status',
      fixed: 'right',
      render: (status: string, record: CommentData) => {
        const badge = (
          <PillsBadge
            title={status || 'Pending'}
            variant={getStatusVariant(status)}
            control={null as any}
            name=""
            className="rounded-xl"
          />
        )

        if (!oogwaiUser) {
          // Just show badge (no dropdown)
          return badge
        }

        return (
          <Dropdown
            trigger={['click']}
            menu={{
              onClick: ({ key }) =>
                handleStatusUpdate(
                  record.chat_id,
                  record.timestamp,
                  key,
                  record.comment
                ),
              selectedKeys: [status],
              items: [
                { key: 'Pending', label: 'Pending' },
                { key: 'Done', label: 'Done' },
                // { key: 'Verified', label: 'Verified' },
              ],
            }}
            disabled={!oogwaiUser}
          >
            <span className="cursor-pointer">{badge}</span>
          </Dropdown>
        )
      },
    },
    {
      title: 'Feedback Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: '12%',
      minWidth: 100,
      render: (timestamp: string) => (timestamp ? dateFormater(timestamp) : '-'),
    },
    {
      key: 'actions',
      fixed: 'right',
      width: '2%',
      render: (_: any, record: CommentData) => (
        <TrashIcon
          className="text-red-500 cursor-pointer hover:text-red-700"
          onClick={() => confirmDelete(record.chat_id, record.timestamp)}
        />
      ),
    },
  ]


  // Filter and search logic
  const filteredAndSearchedData = useMemo(() => {
    let filteredData = feedbackData?.comments || []

    // Apply status filter
    if (statusFilter !== 'All') {
      filteredData = filteredData.filter((comment: CommentData) =>
        comment.comment_status?.toLowerCase() === statusFilter.toLowerCase()
      )
    }

    // Apply search filter
    if (searchTerm) {
      filteredData = filteredData.filter((comment: CommentData) =>
        comment.chat_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comment.comment?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filteredData.map((comment: CommentData, index: number) => ({
      ...comment,
      key: index,
    }))
  }, [feedbackData?.comments, statusFilter, searchTerm])

  const dataSource = filteredAndSearchedData

  return (
    <>
      <Cards variant='secondary' classname='p-0 sm:p-4 md:p-6' childClassName={'flex-col'} title={''} bottomButton={undefined}>
        <div className="flex w-full justify-between items-start border-b border-solid border-gray-300 pb-3 sm:pb-4">
          <div>
            <h4 className="text-[#181D27] font-semibold text-base sm:text-lg">{oogwaiUser ? 'Users' : 'Your'} Feedback History</h4>
            <h5 className="font-normal text-[#535862] text-xs sm:text-sm">You can view {oogwaiUser ? 'users' : ' your'} feedback history here.</h5>
          </div>
        </div>

        {/* Filter and Search Controls */}
        <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 md:gap-4 px-0 md:px-4 py-2 md:py-3">
          <div className="w-full md:w-auto">
            <TabSlider
              size='md'
              tabs={[
                { value: 'All', label: 'All', component: <></> },
                { value: 'Pending', label: 'Pending', component: <></> },
                { value: 'Done', label: 'Done', component: <></> },
                // { value: 'Verified', label: 'Verified', component: <></> }
              ]}
              activeTab={statusFilter}
              setActiveTab={(tab) => setStatusFilter(tab as string)}
              className="w-full md:w-auto md:min-w-[320px] lg:min-w-[400px]"
            />
          </div>
          <div className="w-full md:w-auto md:flex-shrink-0 md:min-w-[280px] lg:min-w-[320px] xl:min-w-[360px]">
            <InputField
              control={control}
              name="search"
              placeholder="Search by Chat ID or Comment..."
              icon={<SearchIcon className="text-gray-500 w-4 h-4" />}
              iconDirection="left"
              className="w-full"
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={dataSource}
          loading={isFeedbackDataLoading}
          pagination={dataSource.length > 10 ? {
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} items`,
            responsive: true,
            size: 'small',
          } : false}
          scroll={{ x: 'max-content' }}
          className="responsive-table"
        />
      </Cards>
      {/* Delete Confirmation Modal */}
      <Modal
        footer={null}
        centered
        width="90%"
        style={{ maxWidth: '500px' }}
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
      >
        <div className="relative p-3 sm:p-4 flex flex-row items-start gap-3 sm:gap-5">
          <IconRings numRings={5} ringBaseSize={40} ringSpacing={48} className="-z-10 hidden sm:block">
            <div className="bg-[#FEE4E2] p-2 sm:p-3 w-fit h-fit rounded-full">
              <TrashIcon className="text-[#D92D20] w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </IconRings>
          <div className="bg-[#FEE4E2] p-2 sm:p-3 w-fit h-fit rounded-full sm:hidden flex-shrink-0">
            <TrashIcon className="text-[#D92D20] w-4 h-4" />
          </div>
          <div className="z-10">
            <h4 className="text-base sm:text-lg">Delete Feedback</h4>
            <p className="text-xs sm:text-sm mt-1">
              Are you sure you want to delete this feedback? This action cannot be undone.
            </p>
          </div>
        </div>
        <div className="p-3 sm:p-4 flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-2 sm:gap-0">
          <div className="flex flex-col sm:flex-row gap-2 sm:space-x-4 w-full sm:w-auto">
            <Button
              variant="outlined"
              onClick={() => setIsDeleteModalOpen(false)}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirmDelete}
              className="w-full sm:w-auto order-1 sm:order-2"
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default FeedbackHistory